import { AfterContentInit, Directive, ElementRef, Input, OnDestroy, SimpleChange } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Subject, debounceTime } from "rxjs";
import { UiTooltipDirective } from "./tooltip.directive";
import { UiTooltipTrigger } from "./tooltip.models";

@UntilDestroy()
@Directive({
  selector: "[ui-tooltip][tooltipIfOverflow]",
  standalone: true,
})
export class TooltipIfOverflowDirective implements AfterContentInit, OnDestroy {
  @Input("tooltipIfOverflow")
  public tooltipTrigger: UiTooltipTrigger;

  private resizeObserver: ResizeObserver;
  private elementResize$ = new Subject<void>();

  constructor(private tooltip: UiTooltipDirective, private elementRef: ElementRef<HTMLElement>) {}

  public ngAfterContentInit(): void {
    this.setTooltipAvailability();
    this.setResizeObserver();
  }

  public ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  private setResizeObserver(): void {
    // use debounceTime(300) to reduce the number of times the config is updated
    // this will cause the config to be updated 300 ms after the resizing stops
    this.elementResize$.pipe(debounceTime(300), untilDestroyed(this)).subscribe(() => this.setTooltipAvailability());
    this.resizeObserver = new ResizeObserver(() => this.elementResize$.next());
    this.resizeObserver.observe(this.elementRef.nativeElement);
  }

  private setTooltipAvailability(): void {
    const { clientWidth, scrollWidth } = this.elementRef.nativeElement;
    const isWidthOverflowing = scrollWidth > clientWidth;

    const previousValue = this.tooltip.trigger;
    this.tooltip.trigger = isWidthOverflowing ? this.tooltipTrigger : null;

    if (previousValue !== this.tooltip.trigger) {
      // needed, as the ng-zorro base tooltip directive updates the config in the on-changes hook
      this.tooltip.ngOnChanges({ trigger: new SimpleChange(previousValue, this.tooltip.trigger, false) });
    }
  }
}
